import { ErrorModal } from "@dexteel/mesf-core";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FieldError } from "react-hook-form";
import { CatalogKeeper } from "../models/CatalogKeeper";
import { getCatalogKeepers } from "../repositories/CatalogKeeperRepository";

type Props = {
	label: string;
	value: number | null;
	onChange?: (value: number | null) => void;
	fieldError?: FieldError | undefined;
	disabled?: boolean;
	required?: boolean;
};

const useKeepers = (onError?: (error: Error) => void) => {
	return useQuery<CatalogKeeper[], Error>({
		queryKey: ["catalogKeepers"],
		queryFn: () => getCatalogKeepers(),
		onError: onError,
	});
};

export const KeeperSelector = ({
	label,
	value,
	onChange = () => {},
	fieldError,
	disabled = false,
	required = false,
}: Props) => {
	const [error, setError] = useState("");

	const { data: keepers, isLoading } = useKeepers((error) =>
		setError(error.message),
	);

	const selectedKeeper = keepers?.find((k) => k.KeeperId === value) || null;

	return (
		<>
			<Autocomplete
				id="keeper-selector"
				fullWidth
				clearOnEscape
				options={keepers || []}
				getOptionLabel={(option) =>
					option.Description
						? `${option.KeeperCode} (${option.Description})`
						: option.KeeperCode
				}
				disabled={disabled}
				renderInput={(params) => (
					<TextField
						{...params}
						required={required}
						label={label}
						error={!!fieldError}
						helperText={fieldError?.message}
						size="small"
						fullWidth
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										params.InputProps.endAdornment
									)}
								</>
							),
						}}
					/>
				)}
				value={selectedKeeper}
				onChange={(event, newValue) => {
					onChange(newValue?.KeeperId ?? null);
				}}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading keepers"
			/>
		</>
	);
};
